import type { Bot, User, WebhookReceives } from '../api/types'
import type { LocalChannel, MemberRow } from '../db/db'
import type { UserMap } from './channels'

// Bots (contract: "Bots"). A bot's user has kind "bot". A managing system's user has kind "integration": it acts on
// memberships but is not a bot and never writes messages.

export function isBotUser(user: Pick<User, 'kind'> | null | undefined): boolean {
  return user?.kind === 'bot'
}

/** A member who is a bot: the member row says so, or (before rows load) the user is a bot. */
export function isBotMember(userId: string, row: Pick<MemberRow, 'source'> | null | undefined, users: UserMap): boolean {
  return row?.source === 'bot' || isBotUser(users.get(userId))
}

/** Member ids that are people, for counts, avatars stacks and presence. */
export function peopleIds(memberIds: string[], users: UserMap): string[] {
  return memberIds.filter((id) => !isBotUser(users.get(id)))
}

export function isBotConversation(channel: Pick<LocalChannel, 'type'>): boolean {
  return channel.type === 'bot_dm'
}

/** People can write in a bot conversation only when the bot has a webhook to receive what they write. */
export function botAcceptsMessages(channel: Pick<LocalChannel, 'type' | 'bot'>): boolean {
  return channel.type === 'bot_dm' && Boolean(channel.bot?.acceptsMessages)
}

/** The bot in a bot conversation: the bot member, else the member who is not me. */
export function botIdOf(channel: Pick<LocalChannel, 'type' | 'memberIds' | 'bot'>, users: UserMap, meId: string | null): string | null {
  if (channel.type !== 'bot_dm') return null
  return channel.bot?.userId ?? channel.memberIds.find((id) => isBotUser(users.get(id))) ?? channel.memberIds.find((id) => id !== meId) ?? null
}

/** The bot's own name in a bot conversation (the conversation title can include the person for admins). */
export function botDisplayName(channel: Pick<LocalChannel, 'type' | 'memberIds' | 'bot'>, users: UserMap, meId: string | null): string {
  const botId = botIdOf(channel, users, meId)
  return (botId && users.get(botId)?.displayName) || 'This bot'
}

/** How the admin lists name a conversation that has no name of its own. */
export function unnamedConversationLabel(type: LocalChannel['type']): string {
  if (type === 'dm') return 'Direct message'
  if (type === 'bot_dm') return 'Bot conversation'
  return 'Group message'
}

/** "Rota sends updates here and can't read replies." */
export function botConversationNote(botName: string): string {
  return `${botName} sends updates here and can't read replies.`
}

/** A Bot from GET /api/bots as a User, for the Avatar component and the profile. */
export function botAsUser(bot: { userId: string; name: string; avatarUrl: string | null; username?: string; description?: string | null }): User {
  return {
    id: bot.userId,
    username: bot.username ?? '',
    displayName: bot.name,
    avatarUrl: bot.avatarUrl,
    isActive: false,
    kind: 'bot',
    linked: true,
    description: bot.description ?? null,
  }
}

export function findBot(bots: Bot[] | null | undefined, userId: string | null | undefined): Bot | undefined {
  return userId ? bots?.find((b) => b.userId === userId) : undefined
}

/** A bot that receives every message in the conversations it is in (people are warned before adding it). */
export function readsEverything(bot: Pick<Bot, 'receives'> | null | undefined): boolean {
  return bot?.receives === 'all'
}

/** Shown before adding a bot that reads every message. */
export function readsEverythingWarning(bot: Pick<Bot, 'name' | 'webhookHost'>): string {
  return `${bot.name} receives every message in this conversation and passes them to ${bot.webhookHost ?? 'its service'}.`
}

export const receivesLabels: Record<WebhookReceives, string> = {
  direct: 'Direct messages only',
  mentions: 'Direct messages, @mentions and replies',
  all: 'Every message in its conversations',
}

const HANDLE_PATTERN = /^[a-z][a-z0-9._-]{1,29}$/
export const RESERVED_HANDLES = ['all', 'here', 'channel', 'everyone', 'admin', 'sysadmin', 'bot', 'system']

/** Why a bot handle is not allowed, or null when it is (contract: handles). Uniqueness is checked by the server. */
export function botHandleProblem(handle: string): string | null {
  if (!HANDLE_PATTERN.test(handle)) return 'Use 2 to 30 lowercase letters, digits, dots, dashes or underscores, starting with a letter.'
  if (RESERVED_HANDLES.includes(handle)) return `"${handle}" is reserved. Choose another handle.`
  return null
}
